export type SowDecision = "APPROVE_FULL" | "APPROVE_WITH_HOLDBACK" | "REQUEST_CHANGES";

export type HomeownerDecisionDraft = {
  drawId: string;
  decision: SowDecision;
  holdbackPercent: number;
  reason: string;
};

export type HomeownerDecisionDrafts = Record<string, HomeownerDecisionDraft>;

export type HomeownerDecisionAmount = { id: string; amount: number };

export type InvoiceGcFeeSnapshot = {
  requestedSubtotal?: number;
  gcFeeAmount?: number;
  gcFeePercent?: number;
};

export type HomeownerDecisionPreview = {
  requestedSubtotal: number;
  requestedGcFee: number;
  requestedTotal: number;
  approvedSubtotal: number;
  approvedGcFee: number;
  approvedTotal: number;
  withheldSubtotal: number;
  withheldGcFee: number;
  withheldTotal: number;
  declinedSubtotal: number;
  declinedGcFee: number;
  declinedTotal: number;
  waitingSubtotal: number;
  reviewedCount: number;
  totalCount: number;
};

type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const DRAFT_STORAGE_PREFIX = "aquadraw:homeowner-review-drafts:v1:";
const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function draftStorageKey(requestId: string) {
  return `${DRAFT_STORAGE_PREFIX}${requestId.trim()}`;
}

const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function invoiceGcFeePercent(invoice?: InvoiceGcFeeSnapshot | null) {
  const explicit = Number(invoice?.gcFeePercent || 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const requestedSubtotal = Number(invoice?.requestedSubtotal || 0);
  const gcFeeAmount = Number(invoice?.gcFeeAmount || 0);
  if (!Number.isFinite(requestedSubtotal) || requestedSubtotal <= 0 || !Number.isFinite(gcFeeAmount) || gcFeeAmount <= 0) return 0;
  return Math.round(((gcFeeAmount / requestedSubtotal) * 100 + Number.EPSILON) * 10_000) / 10_000;
}

/** Mirrors the invoice refresh trigger without mutating or submitting any decision. */
export function calculateHomeownerDecisionPreview(
  draws: HomeownerDecisionAmount[],
  drafts: HomeownerDecisionDrafts,
  gcFeePercent: number,
): HomeownerDecisionPreview {
  const safeGcFeePercent = Number.isFinite(gcFeePercent) && gcFeePercent >= 0 ? gcFeePercent : 0;
  let requestedSubtotal = 0;
  let approvedSubtotal = 0;
  let withheldSubtotal = 0;
  let declinedSubtotal = 0;
  let waitingSubtotal = 0;
  let reviewedCount = 0;

  for (const draw of draws) {
    const amount = roundMoney(Number.isFinite(draw.amount) && draw.amount > 0 ? draw.amount : 0);
    requestedSubtotal = roundMoney(requestedSubtotal + amount);
    const draft = drafts[draw.id];
    if (!draft || draft.drawId !== draw.id) {
      waitingSubtotal = roundMoney(waitingSubtotal + amount);
      continue;
    }

    let validated: HomeownerDecisionDraft;
    try { validated = validateHomeownerDecisionDraft(draft); }
    catch {
      waitingSubtotal = roundMoney(waitingSubtotal + amount);
      continue;
    }
    reviewedCount += 1;

    if (validated.decision === "REQUEST_CHANGES") {
      declinedSubtotal = roundMoney(declinedSubtotal + amount);
      continue;
    }
    const withheld = validated.decision === "APPROVE_WITH_HOLDBACK"
      ? roundMoney(amount * validated.holdbackPercent / 100)
      : 0;
    withheldSubtotal = roundMoney(withheldSubtotal + withheld);
    approvedSubtotal = roundMoney(approvedSubtotal + amount - withheld);
  }

  const decidedSubtotal = roundMoney(approvedSubtotal + withheldSubtotal);
  const requestedGcFee = roundMoney(requestedSubtotal * safeGcFeePercent / 100);
  const decidedGcFee = roundMoney(decidedSubtotal * safeGcFeePercent / 100);
  const withheldGcFee = decidedSubtotal > 0
    ? roundMoney(decidedGcFee * withheldSubtotal / decidedSubtotal)
    : 0;
  const approvedGcFee = roundMoney(decidedGcFee - withheldGcFee);
  const declinedGcFee = waitingSubtotal === 0
    ? roundMoney(requestedGcFee - decidedGcFee)
    : roundMoney(declinedSubtotal * safeGcFeePercent / 100);

  return {
    requestedSubtotal,
    requestedGcFee,
    requestedTotal: roundMoney(requestedSubtotal + requestedGcFee),
    approvedSubtotal,
    approvedGcFee,
    approvedTotal: roundMoney(approvedSubtotal + approvedGcFee),
    withheldSubtotal,
    withheldGcFee,
    withheldTotal: roundMoney(withheldSubtotal + withheldGcFee),
    declinedSubtotal,
    declinedGcFee,
    declinedTotal: roundMoney(declinedSubtotal + declinedGcFee),
    waitingSubtotal,
    reviewedCount,
    totalCount: draws.length,
  };
}

export function decisionDraftProgress(drawIds: string[], drafts: HomeownerDecisionDrafts) {
  const reviewed = drawIds.filter((drawId) => Boolean(drafts[drawId])).length;
  const total = drawIds.length;
  return { reviewed, total, remaining: total - reviewed, complete: total > 0 && reviewed === total };
}

export function validateHomeownerDecisionDraft(draft: HomeownerDecisionDraft): HomeownerDecisionDraft {
  if (!draft.drawId.trim()) throw new Error("SOW invoice line not found.");
  if (!["APPROVE_FULL", "APPROVE_WITH_HOLDBACK", "REQUEST_CHANGES"].includes(draft.decision)) throw new Error("Choose a valid SOW decision.");

  const holdbackPercent = draft.decision === "APPROVE_WITH_HOLDBACK" ? Number(draft.holdbackPercent) : 0;
  const reason = draft.decision === "APPROVE_FULL" ? "" : draft.reason.trim();
  if (draft.decision === "APPROVE_WITH_HOLDBACK" && (!Number.isInteger(holdbackPercent) || holdbackPercent < 1 || holdbackPercent > 15)) {
    throw new Error("Holdback must be a whole percentage between 1 and 15.");
  }
  if (draft.decision !== "APPROVE_FULL" && !reason) {
    throw new Error(draft.decision === "REQUEST_CHANGES" ? "Explain what must change on this SOW." : "Explain why money is being held back on this SOW.");
  }
  if (reason.length > 1000) throw new Error("The SOW explanation must be 1,000 characters or fewer.");

  return { drawId: draft.drawId, decision: draft.decision, holdbackPercent, reason };
}

export function buildHomeownerDecisionBatch(drawIds: string[], drafts: HomeownerDecisionDrafts): HomeownerDecisionDraft[] {
  const uniqueDrawIds = [...new Set(drawIds)];
  if (!uniqueDrawIds.length) throw new Error("No SOW decisions are waiting.");
  const progress = decisionDraftProgress(uniqueDrawIds, drafts);
  if (!progress.complete) throw new Error(`Review all ${progress.total} SOWs before submitting. ${progress.remaining} still need a choice.`);
  return uniqueDrawIds.map((drawId) => {
    const draft = drafts[drawId];
    if (!draft || draft.drawId !== drawId) throw new Error("Each requested SOW must have exactly one decision.");
    return validateHomeownerDecisionDraft(draft);
  });
}

export function homeownerDecisionLabel(draft?: HomeownerDecisionDraft | null) {
  if (!draft) return "Not reviewed";
  if (draft.decision === "APPROVE_FULL") return "Approve in Full";
  if (draft.decision === "APPROVE_WITH_HOLDBACK") return `Approve with ${draft.holdbackPercent}% Holdback`;
  return "Request Changes";
}

export function saveHomeownerDecisionDrafts(
  requestId: string,
  drawIds: string[],
  drafts: HomeownerDecisionDrafts,
  storage: DraftStorage,
  savedAt = Date.now(),
) {
  if (!requestId.trim()) return;
  const allowedDrawIds = new Set(drawIds);
  const safeDrafts: HomeownerDecisionDrafts = {};
  for (const drawId of allowedDrawIds) {
    const draft = drafts[drawId];
    if (!draft || draft.drawId !== drawId) continue;
    try { safeDrafts[drawId] = validateHomeownerDecisionDraft(draft); } catch { /* Invalid drafts never enter recovery storage. */ }
  }
  storage.setItem(draftStorageKey(requestId), JSON.stringify({ requestId, savedAt, drafts: safeDrafts }));
}

export function loadHomeownerDecisionDrafts(
  requestId: string,
  drawIds: string[],
  storage: DraftStorage,
  now = Date.now(),
): HomeownerDecisionDrafts {
  if (!requestId.trim()) return {};
  const key = draftStorageKey(requestId);
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};
    const saved = JSON.parse(raw) as { requestId?: unknown; savedAt?: unknown; drafts?: unknown };
    if (saved.requestId !== requestId || typeof saved.savedAt !== "number" || now - saved.savedAt > DRAFT_MAX_AGE_MS || now < saved.savedAt) {
      storage.removeItem(key);
      return {};
    }
    if (!saved.drafts || typeof saved.drafts !== "object" || Array.isArray(saved.drafts)) throw new Error("Invalid homeowner decision draft storage.");
    const storedDrafts = saved.drafts as Record<string, HomeownerDecisionDraft>;
    const recovered: HomeownerDecisionDrafts = {};
    for (const drawId of new Set(drawIds)) {
      const draft = storedDrafts[drawId];
      if (!draft || draft.drawId !== drawId) continue;
      try { recovered[drawId] = validateHomeownerDecisionDraft(draft); } catch { /* Corrupt choices remain unselected. */ }
    }
    return recovered;
  } catch {
    storage.removeItem(key);
    return {};
  }
}

export function clearHomeownerDecisionDrafts(requestId: string, storage: DraftStorage) {
  if (requestId.trim()) storage.removeItem(draftStorageKey(requestId));
}
